import { ReactNode, useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';

interface PageTransitionProps {
  children: ReactNode;
}

export const PageTransition = ({ children }: PageTransitionProps) => {
  const location = useLocation();
  const [displayChildren, setDisplayChildren] = useState(children);
  const [isVisible, setIsVisible] = useState(true);
  const isFirstRender = useRef(true);
  const previousPathname = useRef(location.pathname);

  useEffect(() => {
    // Skip transition on first render
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    // Only animate when pathname actually changes
    if (previousPathname.current !== location.pathname) {
      previousPathname.current = location.pathname;
      setIsVisible(false);
      
      const timer = setTimeout(() => {
        setDisplayChildren(children);
        setIsVisible(true);
      }, 150);
      
      return () => clearTimeout(timer);
    }
  }, [location.pathname, children]);

  // Update children immediately if pathname hasn't changed (e.g., props update)
  useEffect(() => {
    if (previousPathname.current === location.pathname && !isFirstRender.current) {
      setDisplayChildren(children);
    }
  }, [children, location.pathname]);

  return (
    <div 
      className={`transition-opacity duration-150 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
    >
      {displayChildren}
    </div>
  );
};
